import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/inbound_journey'
const OUTBOUND_DEALS_API_PATH = 'api/v2/admins/outbound/deals'

const API_PATH_BY_NAMESPACE = {
  sales           : 'api/v2/admins/sales',
  product_curator : 'api/v2/admins/product_curators',
  all_team        : 'api/v2/admins/all_team',
  studio          : 'api/v2/admins/studio',
  finance         : 'api/v2/admins/finance',
  buying          : 'api/v2/admins/buying',
  inbound         : 'api/v2/admins/inbound',
  outbound        : 'api/v2/admins/outbound/inbound_journey'
}

const resolveApiPath = (apiNamespace) => API_PATH_BY_NAMESPACE[apiNamespace] || API_PATH

const PRODUCT_FILE_FIELDS = [
  'front_view',
  'right_side_view',
  'rear_view',
  'left_side_view',
  'top_view',
  'bottom_view',
  'comes_with_image',
  'stamp_image',
  'card_image'
]

const PRODUCT_MULTI_FILE_FIELDS = [
  'photo_details'
]

const isUploadFile = (value) => {
  if (!value) return false
  if (typeof File !== 'undefined' && value instanceof File) return true
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true

  return false
}

const cleanProductAttributes = (product = {}) => {
  const cleaned = {}

  Object.keys(product || {}).forEach((key) => {
    const value = product[key]

    if (PRODUCT_FILE_FIELDS.includes(key)) {
      if (isUploadFile(value)) cleaned[key] = value
      return
    }

    if (PRODUCT_MULTI_FILE_FIELDS.includes(key)) {
      const files = Array.from(value || []).filter(isUploadFile)
      if (files.length > 0) cleaned[key] = files
      return
    }

    cleaned[key] = value
  })

  return cleaned
}

const cleanConsignmentPayload = (consignment = {}) => {
  const products = (consignment.product_attributes_list || consignment.products || []).map(cleanProductAttributes)

  return {
    ...consignment,
    product_attributes      : products[0] || cleanProductAttributes(consignment.product_attributes || {}),
    product_attributes_list : products
  }
}

const hasProductUploadFiles = (consignment = {}) => {
  const products = consignment.product_attributes_list || consignment.products || []

  return products.some((product = {}) => {
    const hasSingleFile = PRODUCT_FILE_FIELDS.some((field) => isUploadFile(product[field]))
    const hasMultiFile = PRODUCT_MULTI_FILE_FIELDS.some((field) => Array.from(product[field] || []).some(isUploadFile))

    return hasSingleFile || hasMultiFile
  })
}

const appendValue = (formData, key, value) => {
  if (value === undefined || value === null || value === '') return

  formData.append(key, value)
}

const appendProductAttributes = (formData, product, index) => {
  Object.keys(product || {}).forEach((key) => {
    const value = product[key]

    if (PRODUCT_FILE_FIELDS.includes(key)) {
      if (value) formData.append(`consignment[product_attributes_list][${index}][${key}]`, value)
      return
    }

    if (PRODUCT_MULTI_FILE_FIELDS.includes(key)) {
      Array.from(value || []).forEach((file) => {
        formData.append(`consignment[product_attributes_list][${index}][${key}][]`, file)
      })
      return
    }

    appendValue(formData, `consignment[product_attributes_list][${index}][${key}]`, value)
  })
}

const appendProductFormData = (formData, product = {}) => {
  Object.keys(product || {}).forEach((key) => {
    const value = product[key]

    if (PRODUCT_FILE_FIELDS.includes(key)) {
      if (value) formData.append(`product[${key}]`, value)
      return
    }

    if (PRODUCT_MULTI_FILE_FIELDS.includes(key)) {
      Array.from(value || []).forEach((file) => {
        formData.append(`product[${key}][]`, file)
      })
      return
    }

    appendValue(formData, `product[${key}]`, value)
  })
}

const buildConsignmentFormData = (consignment = {}) => {
  const cleanConsignment = cleanConsignmentPayload(consignment)
  const formData = new FormData()
  const products = cleanConsignment.product_attributes_list || cleanConsignment.products || []

  Object.keys(cleanConsignment).forEach((key) => {
    if ([ 'product_attributes', 'product_attributes_list', 'products' ].includes(key)) return

    appendValue(formData, `consignment[${key}]`, cleanConsignment[key])
  })

  products.forEach((product, index) => appendProductAttributes(formData, product, index))

  return formData
}

const hasUploadFiles = (product = {}) => {
  const hasSingleFile = PRODUCT_FILE_FIELDS.some((field) => isUploadFile(product[field]))
  const hasMultiFile = PRODUCT_MULTI_FILE_FIELDS.some((field) => Array.from(product[field] || []).some(isUploadFile))

  return hasSingleFile || hasMultiFile
}

const buildProductRequestBody = (product = {}) => {
  const cleanProduct = cleanProductAttributes(product)

  if (!hasUploadFiles(cleanProduct)) {
    return {
      body: {
        product: cleanProduct
      },
      config: {}
    }
  }

  const formData = new FormData()
  appendProductFormData(formData, cleanProduct)

  return {
    body: formData,
    config: {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  }
}

const InboundJourneyApiV2 = {

  getConsignments: async ({ q = '', state = '', page = 1, per_page = 20, apiNamespace = 'all_team' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments`
    const params = ParamsUtil.removeEmptyAttributes({
      q        : q,
      state    : state,
      page     : page,
      per_page : per_page
    })

    const response = await axios.get(path, { params })
    return response
  },

  showConsignmentForEdit: async ({ id, apiNamespace = 'all_team' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/${id}`

    const response = await axios.get(path)
    return response
  },

  updateConsignmentConsignor: async ({ id, consignment, apiNamespace = 'all_team' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/${id}`
    const body = {
      consignment: consignment
    }

    const response = await axios.patch(path, body)
    return response
  },

  searchConsignmentUsers: async ({ q = '', apiNamespace = 'all_team' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/users`
    const params = ParamsUtil.removeEmptyAttributes({ q: q })

    const response = await axios.get(path, { params })
    return response
  },

  getConsignmentUserDetails: async ({ user_id, apiNamespace = 'all_team' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/user_details`
    const params = ParamsUtil.removeEmptyAttributes({ user_id: user_id })

    const response = await axios.get(path, { params })
    return response
  },

  getConsignmentOptions: async ({ apiNamespace = '' } = {}) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/options`

    const response = await axios.get(path)
    return response
  },

  createConsignmentWithProduct: async ({ consignment, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments`
    const cleanConsignment = cleanConsignmentPayload(consignment)

    if (hasProductUploadFiles(cleanConsignment)) {
      const formData = buildConsignmentFormData(cleanConsignment)
      const response = await axios.post(path, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response
    }

    const body = {
      consignment: cleanConsignment
    }

    const response = await axios.post(path, body)
    return response
  },

  showConsignment: async ({ id, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/${id}`

    const response = await axios.get(path)
    return response
  },

  goLiveConsignment: async ({ id, location = '', apiNamespace = 'inbound' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/${id}/go_live`
    const body = {
      location: location
    }

    const response = await axios.patch(path, body)
    return response
  },

  createInventoryBatchForConsignment: async ({ consignmentId, handover_to = '', handover_note = '', apiNamespace = 'inbound' }) => {
    const path = `${resolveApiPath(apiNamespace)}/consignments/${consignmentId}/inventory_batch`
    const body = {
      inventory_batch: {
        handover_to: handover_to,
        handover_note: handover_note
      }
    }

    const response = await axios.post(path, body)
    return response
  },

  getInventoryBatches: async ({ q = '', state = '', page = 1, per_page = 20, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/inventory_batches`
    const params = ParamsUtil.removeEmptyAttributes({
      q        : q,
      state    : state,
      page     : page,
      per_page : per_page
    })

    const response = await axios.get(path, { params })
    return response
  },

  showInventoryBatch: async ({ id, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/inventory_batches/${id}`

    const response = await axios.get(path)
    return response
  },

  createInventoryBatch: async ({ consignment_id, handover_to = '', handover_note = '', apiNamespace = 'inbound' }) => {
    const path = `${resolveApiPath(apiNamespace)}/inventory_batches`
    const body = {
      inventory_batch: {
        consignment_id : consignment_id,
        handover_to    : handover_to,
        handover_note  : handover_note
      }
    }

    const response = await axios.post(path, body)
    return response
  },

  cancelInventoryBatch: async ({ id, note, physicalAbsent, expectedProductIds, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/inventory_batches/${id}/cancel_consignment`
    return axios.post(path, { note, physical_absent: physicalAbsent, expected_product_ids: expectedProductIds })
  },

  handoverInventoryBatch: async ({ id, handover_to = 'Studio Team', handover_note = '', apiNamespace = 'inbound' }) => {
    const path = `${resolveApiPath(apiNamespace)}/inventory_batches/${id}/handover_to_studio`
    const body = {
      inventory_batch: {
        handover_to: handover_to,
        handover_note: handover_note
      }
    }

    const response = await axios.patch(path, body)
    return response
  },


  getStoreCategories: async ({ apiNamespace = '' } = {}) => {
    const path = `${resolveApiPath(apiNamespace)}/store_categories`

    const response = await axios.get(path)
    return response
  },

  getTandaMasuks: async ({ specific_date = '', search = '', page = 1, per_page = 20, apiNamespace = '' }) => {
    const path   = `${resolveApiPath(apiNamespace)}/tanda_masuks`
    const params = ParamsUtil.removeEmptyAttributes({
      specific_date : specific_date,
      search        : search,
      page          : page,
      per_page      : per_page
    })

    const response = await axios.get(path, { params })
    return response
  },

  showTandaMasuk: async ({ id, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/tanda_masuks/${id}`

    const response = await axios.get(path)
    return response
  },

  createTandaMasuk: async ({ tanda_masuk, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/tanda_masuks`
    const body = {
      tanda_masuk: tanda_masuk
    }

    const response = await axios.post(path, body)
    return response
  },

  deleteTandaMasuk: async ({ id, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/tanda_masuks/${id}`

    const response = await axios.delete(path)
    return response
  },

  downloadTandaMasukPdf: async ({ id, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/tanda_masuks/${id}/download_pdf`

    const response = await axios.get(path, { responseType: 'blob' })
    return response
  },


  getAuditLogEntries: async ({ q = '', scope = 'inbound_journey', activity = '', done_by = '', product_id = '', from_date = '', to_date = '', page = 1, per_page = 20 }) => {
    const path   = `${API_PATH}/audit_log_entries`
    const params = ParamsUtil.removeEmptyAttributes({
      q          : q,
      scope      : scope,
      activity   : activity,
      done_by    : done_by,
      product_id : product_id,
      from_date  : from_date,
      to_date    : to_date,
      page       : page,
      per_page   : per_page
    })

    const response = await axios.get(path, { params })
    return response
  },

  showAuditLogEntry: async ({ id }) => {
    const path = `${API_PATH}/audit_log_entries/${id}`

    const response = await axios.get(path)
    return response
  },

  getProducts: async ({ scope = '', q = '', page = 1, per_page = 20, appraisalCategory = '', internalCategory = '', productCuratorPic = '', pendingTask = '', excludeTandaMasukAssigned = false, apiNamespace = '' }) => {
    const path   = `${resolveApiPath(apiNamespace)}/products`
    const params = ParamsUtil.removeEmptyAttributes({
      scope              : scope,
      q                  : q,
      page               : page,
      per_page           : per_page,
      appraisal_category : appraisalCategory,
      internal_category  : internalCategory,
      product_curator_pic : productCuratorPic,
      pending_task        : pendingTask,
      exclude_tanda_masuk_assigned: excludeTandaMasukAssigned ? '1' : ''
    })

    const response = await axios.get(path, { params })
    return response
  },

  showProduct: async ({ id, apiNamespace = '', includeOptions = false }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}`
    const params = ParamsUtil.removeEmptyAttributes({
      include_options: includeOptions ? '1' : ''
    })

    const response = await axios.get(path, { params })
    return response
  },

  updateProduct: async ({ id, product, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}`
    const { body, config } = buildProductRequestBody(product)

    const response = await axios.patch(path, body, config)
    return response
  },

  studioComplete: async ({ id, product, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/studio_complete`
    const { body, config } = buildProductRequestBody(product)

    const response = await axios.patch(path, body, config)
    return response
  },

  fillAppraisal: async ({ id, product, skip_confirm_buying_price = false, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/fill_appraisal`
    const body = {
      product: product,
      skip_confirm_buying_price: skip_confirm_buying_price ? '1' : '0'
    }

    const response = await axios.patch(path, body)
    return response
  },

  confirmBuyingPrice: async ({ id, product, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/confirm_buying_price`
    const body = {
      product: product
    }

    const response = await axios.patch(path, body)
    return response
  },

  rejectBuyingPrice: async ({ id, appraisal_note, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/reject_buying_price`
    const body = {
      product: {
        appraisal_note: appraisal_note
      }
    }

    const response = await axios.patch(path, body)
    return response
  },

  approveAppraisal: async ({ id, product = {}, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/approve_appraisal`
    const body = {
      product: product
    }

    const response = await axios.patch(path, body)
    return response
  },

  bulkApproveAppraisal: async ({ product_ids, products = [], apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/bulk_approve_appraisal`
    const body = {
      product_ids: product_ids,
      products: products
    }

    const response = await axios.patch(path, body)
    return response
  },

  rejectAppraisal: async ({ id, appraisal_note, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/reject_appraisal`
    const body = {
      product: {
        appraisal_note: appraisal_note
      }
    }

    const response = await axios.patch(path, body)
    return response
  },

  finalCheck: async ({ id, product, apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/final_check`
    const body = {
      product: product
    }

    const response = await axios.patch(path, body)
    return response
  },

  goLive: async ({ id, location = '', apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/${id}/go_live`
    const body = {
      location: location
    }

    const response = await axios.patch(path, body)
    return response
  },

  priceTagData: async ({ q, auditAction = '', slotNumber = null, previousSku = '', apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/price_tag_data`
    const params = ParamsUtil.removeEmptyAttributes({
      q            : q,
      audit_action : auditAction,
      slot_number  : slotNumber,
      previous_sku : previousSku
    })

    const response = await axios.get(path, { params })
    return response
  },

  priceTagAudit: async ({ event, products = [], triggerSource = '', apiNamespace = '' }) => {
    const path = `${resolveApiPath(apiNamespace)}/products/price_tag_audit`
    const body = {
      price_tag_audit: {
        event          : event,
        trigger_source : triggerSource,
        products       : products
      }
    }

    const response = await axios.post(path, body)
    return response
  },

  authenticateCartItem: async ({ dealId, cartItemId, state, authenticator_name, note, apiNamespace = 'outbound' }) => {
    const path = `${resolveApiPath(apiNamespace)}/deals/${dealId}/cart_items/${cartItemId}/authenticate`
    const body = {
      state: state,
      authenticator_name: authenticator_name,
      note: note
    }

    const response = await axios.patch(path, body)
    return response
  },

  searchDeals: async ({ q = '', scope = '', page = 1, per_page = 10 }) => {
    const params = ParamsUtil.removeEmptyAttributes({
      q        : q,
      scope    : scope,
      page     : page,
      per_page : per_page
    })

    const response = await axios.get(OUTBOUND_DEALS_API_PATH, { params })
    return response
  },

  showDeal: async ({ dealId }) => {
    const path = `${OUTBOUND_DEALS_API_PATH}/${dealId}`

    const response = await axios.get(path)
    return response
  }
}

export default InboundJourneyApiV2
